import { TopPage } from "@/components/elements/TopPage";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Box } from "@mui/material";
import { FC, useMemo, useState, useEffect } from "react";
import { matchRoutes, Outlet, useLocation, useParams, useNavigate } from "react-router-dom";
import { contactMenu, pageHideTabs } from "@/features/contact-type/constants/contactMenu";
import { AppRoutes } from "@/routes/config";
import { GenericTabs } from "@/components/commons/GenericTabs";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useContactListSessionStorage } from "@/hooks/useContactListSessionStorage";

type TContactLayoutProps = {};

export const ContactLayout: FC<TContactLayoutProps> = () => {
  const { id } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveState } = useContactListSessionStorage();

  const activeIndex = contactMenu.findIndex(
    ({ url, extraUrls }) =>
      matchRoutes([{ path: url }], pathname) !== null || extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null),
  );

  const [value, setValue] = useState(activeIndex !== -1 ? activeIndex : 0);

  useEffect(() => {
    setValue(activeIndex !== -1 ? activeIndex : 0);
  }, [activeIndex]);

  useEffect(() => {
    const isListPage = pathname === AppRoutes.unresolvedContact || pathname === AppRoutes.resolvedContact;
    if (isListPage) {
      saveState({
        activeTabUrl: pathname,
        searchParams: search.startsWith("?") ? search.slice(1) : search,
      });
    }
  }, [pathname, search, saveState]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    event.preventDefault();
    setValue(newValue);
    const selectedMenu = contactMenu[newValue];
    if (selectedMenu && selectedMenu.url) {
      if (selectedMenu.url === AppRoutes.unresolvedContact || selectedMenu.url === AppRoutes.resolvedContact) {
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST], refetchType: "all" });

        if (selectedMenu.url === AppRoutes.unresolvedContact) {
          queryClient.refetchQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST] });
        }
      } else if (selectedMenu.url === AppRoutes.contactType) {
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.CONTACT_TYPE.FETCH_LIST] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT_TYPE.FETCH_LIST], refetchType: "active" });
      }
      navigate(getDynamicRoute(selectedMenu.url, { id }));
    }
  };

  const isExtraUrlActive = useMemo(
    () => contactMenu.some(({ extraUrls }) => extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null)),
    [pathname],
  );

  const backUrl = useMemo(() => {
    if (isExtraUrlActive) {
      const activeMenu = contactMenu.find(({ extraUrls }) => extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null));
      return activeMenu ? getDynamicRoute(activeMenu.url, { id }) : AppRoutes.duringContractList;
    }
    return AppRoutes.duringContractList;
  }, [isExtraUrlActive, pathname, id]);

  const isPageHideTab = useMemo(() => pageHideTabs.some((tab) => matchRoutes([{ path: tab }], pathname) !== null), [pathname]);

  return (
    <Stack sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        {isExtraUrlActive ? (
          <TopPage title="お問い合わせ管理" backUrl={backUrl} enablePageBack={false} />
        ) : (
          <TopPage title="お問い合わせ管理" enablePageBack={false} />
        )}
      </Box>

      {!isPageHideTab && (
        <GenericTabs tabs={contactMenu.map(({ title, url }) => ({ label: title, url }))} value={value} onChange={handleChange} id={id} />
      )}

      <Outlet />
    </Stack>
  );
};
